import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStore } from './tokenStore';
import { authEvents } from './authEvents';
import type { WaiterSession } from './session.types';
import { authService } from '../api/services/authService';
import type { LoginRequest } from '../api/types/auth.types';

export interface SessionState {
  session: WaiterSession | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  login: (req: LoginRequest) => Promise<WaiterSession>;
  logout: () => Promise<void>;
}

export const SessionCtx = createContext<SessionState | null>(null);

/**
 * Reads any session persisted to sessionStorage during a prior render of this
 * tab. Returns null if either the session JSON or the access token is missing.
 */
function readInitialSession(): WaiterSession | null {
  const persisted = tokenStore.getPersistedSession();
  const access = tokenStore.getAccessToken();
  if (!persisted || !access) return null;
  return persisted;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  // Restore synchronously so an F5 does not flash the login screen.
  const [session, setSession] = useState<WaiterSession | null>(readInitialSession);

  // There is no "session context" endpoint on crs-api: a refresh token alone
  // cannot rebuild the session (roles come from the login pipeline). So the
  // app never bootstraps asynchronously — it is either restored or logged out.
  const [isBootstrapping] = useState<boolean>(false);

  const commitSession = useCallback((next: WaiterSession | null) => {
    setSession(next);
    tokenStore.setPersistedSession(next);
  }, []);

  // Every cached query belongs to the signed-in waiter and their outlet; the
  // query client outlives the session, so it is emptied whenever the session
  // ends, or the next waiter on this tab would see the last one's data.
  const qc = useQueryClient();

  // The axios interceptor emits 'cleared' when a silent refresh fails.
  useEffect(() => {
    return authEvents.on('cleared', () => {
      qc.clear();
      commitSession(null);
    });
  }, [commitSession, qc]);

  const login = useCallback(
    async (req: LoginRequest) => {
      const next = await authService.login(req);
      commitSession(next);
      return next;
    },
    [commitSession],
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      tokenStore.clear(); // also drops the outlet and section
      qc.clear();
      commitSession(null);
    }
  }, [commitSession, qc]);

  const value = useMemo<SessionState>(
    () => ({
      session,
      isAuthenticated: session !== null,
      isBootstrapping,
      login,
      logout,
    }),
    [session, isBootstrapping, login, logout],
  );

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}
